<template>
    <div class="min-h-screen py-6 px-0 sm:px-6 lg:px-0">
        <!-- Header with Gradient -->
        <div class="max-w-7xl mx-auto mb-6">
            <div
                class="bg-gradient-to-r bg-white rounded-xl shadow p-6 text-black"
            >
                <div
                    class="flex flex-col md:flex-row md:items-center md:justify-between"
                >
                    <div class="flex items-center space-x-4">
                        <router-link
                            to="/"
                            class="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                        >
                            <i class="bx bx-arrow-back text-xl"></i>
                        </router-link>
                        <div>
                            <h1 class="text-xl md:text-2xl font-bold">
                                {{ account ? account.name : 'Account Details' }}
                            </h1>
                            <p class="mt-1 text-sm text-gray-500">
                                Transaction Statement &amp; Activity
                            </p>
                        </div>
                    </div>
                    <div
                        v-if="account"
                        class="mt-4 md:mt-0 text-left md:text-right"
                    >
                        <p class="text-sm text-gray-500">Current Balance</p>
                        <p class="text-xl md:text-2xl font-bold">
                            GMD {{ formatPrice(account.current_balance) }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Account Info Pills -->
        <div v-if="account" class="max-w-7xl mx-auto mb-6">
            <div class="flex flex-wrap gap-3">
                <div
                    class="inline-flex items-center px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200"
                >
                    <i class="bx bx-id-card text-green-600 mr-2"></i>
                    <span class="text-sm text-gray-600">Account ID:</span>
                    <span class="text-sm font-semibold text-gray-900 ml-1">{{
                        account.id
                    }}</span>
                </div>
                <div
                    class="inline-flex items-center px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200"
                >
                    <i class="bx bx-calendar text-green-600 mr-2"></i>
                    <span class="text-sm text-gray-600">Period:</span>
                    <span class="text-sm font-semibold text-gray-900 ml-1"
                        >{{ period.from }} → {{ period.to }}</span
                    >
                </div>
                <div
                    v-if="summary"
                    class="inline-flex items-center px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200"
                >
                    <i class="bx bx-transfer text-green-600 mr-2"></i>
                    <span class="text-sm text-gray-600">Transactions:</span>
                    <span class="text-sm font-semibold text-gray-900 ml-1">{{
                        summary.transaction_count
                    }}</span>
                </div>
            </div>
        </div>

        <!-- Filters -->
        <div class="max-w-7xl mx-auto mb-6">
            <div
                class="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
            >
                <div class="flex items-center mb-4">
                    <i class="bx bx-filter-alt text-green-600 text-xl mr-2"></i>
                    <h3 class="text-sm font-semibold text-gray-700">
                        Filter Transactions
                    </h3>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label
                            class="block text-xs font-medium text-gray-600 mb-1"
                            >From Date</label
                        >
                        <input
                            type="date"
                            v-model="filters.from"
                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        />
                    </div>
                    <div>
                        <label
                            class="block text-xs font-medium text-gray-600 mb-1"
                            >To Date</label
                        >
                        <input
                            type="date"
                            v-model="filters.to"
                            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        />
                        <p
                            v-if="!dateRangeValid"
                            class="mt-1 text-xs text-red-500"
                        >
                            <i class="bx bx-error-circle mr-1"></i>Max 1 year
                            allowed
                        </p>
                    </div>
                    <div class="flex items-end">
                        <button
                            @click="fetchTransactions"
                            :disabled="loading || !dateRangeValid"
                            class="w-full px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                        >
                            <i class="bx bx-search mr-2"></i>
                            <span v-if="!loading">Apply Filter</span>
                            <span v-else>Loading...</span>
                        </button>
                    </div>
                    <div class="flex items-end">
                        <button
                            @click="exportToCSV"
                            :disabled="loading || transactions.length === 0"
                            class="w-full px-4 py-2.5 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                        >
                            <i class="bx bx-download mr-2"></i> Export CSV
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Summary Cards -->
        <div v-if="summary" class="max-w-7xl mx-auto mb-6">
            <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div
                    class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                    <div class="flex flex-col">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg mb-3"
                        >
                            <i class="bx bx-wallet text-gray-600 text-xl"></i>
                        </div>
                        <p
                            class="text-xs text-gray-500 font-medium uppercase tracking-wide"
                        >
                            Opening Balance
                        </p>
                        <p
                            class="text-lg md:text-xl font-bold text-gray-900 mt-1"
                        >
                            {{ formatPrice(summary.opening_balance) }}
                        </p>
                    </div>
                </div>
                <div
                    class="bg-white rounded-xl shadow-sm border border-green-200 p-4 hover:shadow-md transition-shadow"
                >
                    <div class="flex flex-col">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg mb-3"
                        >
                            <i
                                class="bx bx-trending-up text-green-600 text-xl"
                            ></i>
                        </div>
                        <p
                            class="text-xs text-gray-500 font-medium uppercase tracking-wide"
                        >
                            Total Credits
                        </p>
                        <p
                            class="text-lg md:text-xl font-bold text-green-600 mt-1"
                        >
                            +{{ formatPrice(summary.total_credits) }}
                        </p>
                    </div>
                </div>
                <div
                    class="bg-white rounded-xl shadow-sm border border-red-200 p-4 hover:shadow-md transition-shadow"
                >
                    <div class="flex flex-col">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg mb-3"
                        >
                            <i
                                class="bx bx-trending-down text-red-600 text-xl"
                            ></i>
                        </div>
                        <p
                            class="text-xs text-gray-500 font-medium uppercase tracking-wide"
                        >
                            Total Debits
                        </p>
                        <p
                            class="text-lg md:text-xl font-bold text-red-600 mt-1"
                        >
                            -{{ formatPrice(summary.total_debits) }}
                        </p>
                    </div>
                </div>
                <div
                    class="bg-white rounded-xl shadow-sm border border-green-300 p-4 hover:shadow-md transition-shadow bg-gradient-to-br from-white to-green-50"
                >
                    <div class="flex flex-col">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-green-200 rounded-lg mb-3"
                        >
                            <i
                                class="bx bx-check-circle text-green-700 text-xl"
                            ></i>
                        </div>
                        <p
                            class="text-xs text-gray-500 font-medium uppercase tracking-wide"
                        >
                            Closing Balance
                        </p>
                        <p
                            class="text-lg md:text-xl font-bold text-green-700 mt-1"
                        >
                            {{ formatPrice(summary.closing_balance) }}
                        </p>
                    </div>
                </div>
                <div
                    class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                    <div class="flex flex-col">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mb-3"
                        >
                            <i class="bx bx-list-ul text-blue-600 text-xl"></i>
                        </div>
                        <p
                            class="text-xs text-gray-500 font-medium uppercase tracking-wide"
                        >
                            Transactions
                        </p>
                        <p
                            class="text-lg md:text-xl font-bold text-gray-900 mt-1"
                        >
                            {{ summary.transaction_count }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-if="error" class="max-w-7xl mx-auto mb-6">
            <div class="bg-red-50 border border-red-200 rounded-xl p-4">
                <div class="flex items-center">
                    <div
                        class="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3"
                    >
                        <i class="bx bx-error-circle text-red-600 text-xl"></i>
                    </div>
                    <div>
                        <p class="text-sm font-medium text-red-800">
                            Error Loading Data
                        </p>
                        <p class="text-sm text-red-600">{{ error }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="max-w-7xl mx-auto">
            <div
                class="bg-white rounded-xl shadow-sm border border-gray-200 p-12"
            >
                <div class="flex flex-col items-center justify-center">
                    <div
                        class="animate-spin rounded-full h-12 w-12 border-4 border-green-200 border-t-green-600 mb-4"
                    ></div>
                    <p class="text-gray-600 font-medium">
                        Loading transactions...
                    </p>
                    <p class="text-gray-400 text-sm mt-1">Please wait</p>
                </div>
            </div>
        </div>

        <!-- Transactions Table -->
        <div
            v-else-if="!loading && transactions.length > 0"
            class="max-w-7xl mx-auto"
        >
            <div
                class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
            >
                <div
                    class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white"
                >
                    <div class="flex items-center justify-between">
                        <div class="flex items-center">
                            <i
                                class="bx bx-history text-green-600 text-xl mr-2"
                            ></i>
                            <h3 class="text-sm font-semibold text-gray-700">
                                Transaction History
                            </h3>
                        </div>
                        <div class="flex items-center space-x-2">
                            <span
                                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                            >
                                {{ transactions.length }} of
                                {{ pagination.total }}
                            </span>
                            <span
                                v-if="pagination.last_page > 1"
                                class="text-xs text-gray-500"
                            >
                                Page {{ pagination.current_page }} of
                                {{ pagination.last_page }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="overflow-auto max-h-[60vh]">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <th
                                    class="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50"
                                >
                                    Date &amp; Time
                                </th>
                                <th
                                    class="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50"
                                >
                                    Description
                                </th>
                                <th
                                    class="px-6 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50"
                                >
                                    Credit
                                </th>
                                <th
                                    class="px-6 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50"
                                >
                                    Debit
                                </th>
                                <th
                                    class="px-6 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50"
                                >
                                    Balance
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-100">
                            <tr
                                v-for="txn in transactions"
                                :key="txn.id"
                                class="hover:bg-green-50/50 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900"
                                    >
                                        {{ formatDate(txn.date_time) }}
                                    </div>
                                    <div class="text-xs text-gray-400">
                                        {{ formatTime(txn.date_time) }}
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div
                                        class="text-sm text-gray-900 max-w-xs truncate"
                                    >
                                        {{ txn.description }}
                                    </div>
                                    <div class="text-xs text-gray-400">
                                        Ref: #{{ txn.id }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <span
                                        v-if="txn.credit"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-700"
                                    >
                                        +{{ formatPrice(txn.credit) }}
                                    </span>
                                    <span v-else class="text-sm text-gray-300"
                                        >—</span
                                    >
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <span
                                        v-if="txn.debit"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-700"
                                    >
                                        -{{ formatPrice(txn.debit) }}
                                    </span>
                                    <span v-else class="text-sm text-gray-300"
                                        >—</span
                                    >
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold text-gray-900"
                                    >
                                        {{ formatPrice(txn.balance) }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                        <!-- Totals Footer -->
                        <tfoot
                            class="bg-gradient-to-r from-gray-100 to-gray-50 border-t-2 border-gray-300 sticky bottom-0"
                        >
                            <tr class="font-bold">
                                <td
                                    class="px-6 py-4 whitespace-nowrap"
                                    colspan="2"
                                >
                                    <div class="flex items-center">
                                        <i
                                            class="bx bx-calculator text-gray-500 mr-2"
                                        ></i>
                                        <div>
                                            <div
                                                class="text-sm font-bold text-gray-900"
                                            >
                                                TOTALS
                                            </div>
                                            <div class="text-xs text-gray-500">
                                                {{
                                                    summary
                                                        ? summary.transaction_count
                                                        : 0
                                                }}
                                                transactions
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold text-green-600"
                                    >
                                        +{{
                                            formatPrice(
                                                summary
                                                    ? summary.total_credits
                                                    : 0
                                            )
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div class="text-sm font-bold text-red-600">
                                        -{{
                                            formatPrice(
                                                summary
                                                    ? summary.total_debits
                                                    : 0
                                            )
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold text-green-700"
                                    >
                                        {{
                                            formatPrice(
                                                summary
                                                    ? summary.closing_balance
                                                    : 0
                                            )
                                        }}
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <!-- Load More Button -->
                <div
                    v-if="pagination.has_more"
                    class="px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white text-center"
                >
                    <button
                        @click="loadMore"
                        :disabled="loadingMore"
                        class="inline-flex items-center px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        <span v-if="!loadingMore">
                            <i class="bx bx-down-arrow-alt mr-2"></i>
                            Load More ({{
                                pagination.total - transactions.length
                            }}
                            remaining)
                        </span>
                        <span v-else>
                            <i class="bx bx-loader-alt bx-spin mr-2"></i>
                            Loading...
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div
            v-else-if="!loading && transactions.length === 0 && !error"
            class="max-w-7xl mx-auto"
        >
            <div
                class="bg-white rounded-xl shadow-sm border border-gray-200 p-12"
            >
                <div class="text-center">
                    <div
                        class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
                    >
                        <i class="bx bx-receipt text-4xl text-gray-400"></i>
                    </div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">
                        No Transactions Found
                    </h3>
                    <p class="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                        No transaction activity found for the selected date
                        range. Try adjusting your filters.
                    </p>
                    <button
                        @click="resetFilters"
                        class="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <i class="bx bx-refresh mr-2"></i>
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    middleware: 'auth',
    name: 'AccountDetails',

    data() {
        return {
            account: null,
            transactions: [],
            summary: null,
            period: {
                from: '',
                to: '',
            },
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 100,
                total: 0,
                has_more: false,
            },
            loading: false,
            loadingMore: false,
            error: null,
            filters: {
                from: '',
                to: '',
            },
        };
    },

    computed: {
        accountId() {
            return this.$route.params.id;
        },

        dateRangeValid() {
            if (!this.filters.from || !this.filters.to) return true;
            const from = new Date(this.filters.from);
            const to = new Date(this.filters.to);
            const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
            return diffDays <= 365;
        },
    },

    created() {
        this.setDefaultDates();
        this.fetchTransactions();
    },

    methods: {
        setDefaultDates() {
            const today = new Date();
            const todayStr = today.toISOString().split('T')[0];
            this.filters.from = todayStr;
            this.filters.to = todayStr;
        },

        resetFilters() {
            this.setDefaultDates();
            this.fetchTransactions();
        },

        async fetchTransactions(page = 1, append = false) {
            // Validate date range (max 1 year)
            if (!this.dateRangeValid) {
                this.error = 'Date range cannot exceed 1 year.';
                return;
            }

            if (append) {
                this.loadingMore = true;
            } else {
                this.loading = true;
                this.transactions = [];
            }
            this.error = null;

            try {
                const params = {
                    from: this.filters.from,
                    to: this.filters.to,
                    page: page,
                    per_page: this.pagination.per_page,
                };

                const response = await axios.get(
                    `/accounts/${this.accountId}/transactions`,
                    { params }
                );

                // Map API response to component data
                this.account = response.data.account;
                this.period = response.data.period;

                // Summary is always for the FULL date range (same for all pages)
                if (!append) {
                    this.summary = response.data.summary;
                }

                // Append or replace transactions
                if (append) {
                    this.transactions = [
                        ...this.transactions,
                        ...(response.data.transactions || []),
                    ];
                } else {
                    this.transactions = response.data.transactions || [];
                }

                // Update pagination info
                if (response.data.pagination) {
                    this.pagination = {
                        current_page: response.data.pagination.current_page,
                        last_page: response.data.pagination.last_page,
                        per_page: response.data.pagination.per_page,
                        total: response.data.pagination.total,
                        has_more: response.data.pagination.has_more,
                    };
                }
            } catch (err) {
                console.error('Error fetching transactions:', err);

                // Let global interceptor handle 401
                if (err.response?.status === 401) {
                    return;
                }

                if (err.response) {
                    switch (err.response.status) {
                        case 404:
                            this.error = 'Account not found.';
                            break;
                        case 400:
                            this.error =
                                err.response.data?.message ||
                                'Date range cannot exceed 1 year.';
                            break;
                        case 500:
                            this.error =
                                'Server error. Please try again later.';
                            break;
                        default:
                            this.error = 'Failed to load transactions.';
                    }
                } else {
                    this.error = 'Network error. Please check your connection.';
                }
            } finally {
                this.loading = false;
                this.loadingMore = false;
            }
        },

        async loadMore() {
            if (this.pagination.has_more && !this.loadingMore) {
                await this.fetchTransactions(
                    this.pagination.current_page + 1,
                    true
                );
            }
        },

        formatDate(dateTime) {
            if (!dateTime) return '';
            const date = new Date(dateTime);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        },

        formatTime(dateTime) {
            if (!dateTime) return '';
            const date = new Date(dateTime);
            return date.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        },

        formatPrice(value) {
            if (!value && value !== 0) return '0.00';
            const val = parseFloat(value).toFixed(2);
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },

        exportToCSV() {
            if (!this.transactions.length) return;

            const headers = [
                'ID',
                'Date & Time',
                'Description',
                'Credit',
                'Debit',
                'Balance',
            ];

            const rows = this.transactions.map(txn => {
                return [
                    txn.id,
                    txn.date_time,
                    `"${txn.description}"`,
                    txn.credit || '',
                    txn.debit || '',
                    txn.balance,
                ];
            });

            // Add summary rows
            rows.push([]);
            rows.push(['SUMMARY']);
            rows.push([
                'Opening Balance',
                '',
                '',
                '',
                '',
                this.summary?.opening_balance || 0,
            ]);
            rows.push([
                'Total Credits',
                '',
                '',
                this.summary?.total_credits || 0,
                '',
                '',
            ]);
            rows.push([
                'Total Debits',
                '',
                '',
                '',
                this.summary?.total_debits || 0,
                '',
            ]);
            rows.push([
                'Closing Balance',
                '',
                '',
                '',
                '',
                this.summary?.closing_balance || 0,
            ]);

            const csvContent = [
                `Account: ${this.account?.name || ''} (ID: ${
                    this.account?.id || ''
                })`,
                `Period: ${this.period?.from || ''} to ${
                    this.period?.to || ''
                }`,
                '',
                headers.join(','),
                ...rows.map(row => row.join(',')),
            ].join('\n');

            const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${this.account?.name || 'account'}_statement_${
                this.filters.from
            }_to_${this.filters.to}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        },
    },
};
</script>
